/**
 * SspStatusPill — live closed-loop status indicator for the Investor Overview.
 *
 * Polls /api/brand/ssp/health every 30s and renders a 1-pixel-pulse pill that
 * shows VCs the DSP↔SSP integration is wired and healthy in real-time:
 *
 *   • Green "Closed-loop wired"  → all 4 contract endpoints have been signed-and-verified
 *                                  AND DNA v2 + HMAC are configured
 *   • Amber "Partially wired"    → some endpoints err'd recently OR signing/DNA degraded
 *   • Red   "Disconnected"       → SSP is unreachable / disabled
 *
 * Click → tooltip shows the per-endpoint counters + DNA semantic config + HMAC
 * signing flag, so investors who want to dig in can see the actual proof.
 *
 * This is the literal proof-of-life for the closed loop on the public share link.
 */
import React, { useEffect, useState } from 'react';
import { Activity, Check, AlertCircle, Sparkles, Lock, RefreshCw } from 'lucide-react';
import { getSspHealth } from '../../services/dsp';

const TEAL  = '#00D4FF';
const PINK  = '#FF4D94';
const GREEN = '#4ade80';
const AMBER = '#FFC83D';
const RED   = '#FF6B6B';
const BORDER_DIM = '#1F1F1F';

const POLL_MS = 30000;

function summarize(health) {
  // Returns { kind: 'green'|'amber'|'red', label, detail }
  if (!health) return { kind: 'red', label: 'Loading…', detail: null };
  if (!health.enabled || !health.ssp_base_configured) {
    return { kind: 'red', label: 'Disconnected', detail: 'SSP integration disabled' };
  }
  const ep = health.endpoints || {};
  // Each endpoint can be (a) untested (ok=0,err=0), (b) green (last_error=null),
  // (c) amber (has had errs but recovered), or (d) red (last_error present).
  const states = Object.values(ep);
  const anyLastError = states.some((s) => s?.last_error);
  const totalOk = states.reduce((a, s) => a + (s?.ok_count || 0), 0);
  const totalErr = states.reduce((a, s) => a + (s?.err_count || 0), 0);

  // DNA + HMAC secondary signals
  const dnaOk = !!health.dna?.ok && health.dna?.semantic?.configured;
  const hmacOk = !!health.hmac_signing;

  // iter-92.9.4 — brand_embedding rescue rate as an early-warning signal.
  // A sustained 25%+ rescue rate means the SSP's OpenAI dependency is
  // degraded enough that ranking quality is at risk. Escalate the pill.
  const rescueLevel = health.brand_embedding_rescue?.level;
  const rescueRatePct = health.brand_embedding_rescue?.rate
    ? `${(health.brand_embedding_rescue.rate * 100).toFixed(0)}%`
    : null;

  if (rescueLevel === 'incident') {
    return {
      kind: 'red',
      label: 'SSP embedding degraded',
      detail: `${rescueRatePct} rescue rate · OpenAI dependency flaky`,
    };
  }
  if (anyLastError) return { kind: 'amber', label: 'Recovering', detail: 'recent error · auto-retrying' };
  if (totalOk === 0 && totalErr === 0) {
    // Process just restarted or no traffic yet — still proves the wire is up
    return { kind: 'amber', label: 'Idle · wired', detail: '0 calls since last restart' };
  }
  if (rescueLevel === 'warning') {
    return {
      kind: 'amber',
      label: 'Wired · rescue elevated',
      detail: `${rescueRatePct} of pushes needed retry — watch closely`,
    };
  }
  if (!dnaOk || !hmacOk) {
    return { kind: 'amber', label: 'Wired · partial', detail: !dnaOk ? 'Digital DNA not configured' : 'HMAC signing off' };
  }
  return {
    kind: 'green',
    label: 'Closed-loop wired',
    detail: `${totalOk} calls verified · Digital DNA · HMAC signed`,
  };
}

export default function SspStatusPill() {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    let timer = null;

    const tick = async () => {
      try {
        const data = await getSspHealth();
        if (mounted) {
          setHealth(data);
          setLoaded(true);
        }
      } catch (_e) {
        if (mounted) {
          setHealth(null);
          setLoaded(true);
        }
      }
    };
    tick();
    timer = setInterval(tick, POLL_MS);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!loaded) return null;

  const { kind, label, detail } = summarize(health);
  const color = kind === 'green' ? GREEN : kind === 'amber' ? AMBER : RED;
  const ep = health?.endpoints || {};

  return (
    <div className="relative" data-testid="overview-ssp-status">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-[#0F0F0F] font-bold text-[10px] tracking-wider transition-all hover:bg-[#141414]"
        style={{ borderColor: `${color}55`, color }}
        title={`${label} — ${detail || 'click for detail'}`}
        data-testid="overview-ssp-status-pill"
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{
            background: color,
            boxShadow: `0 0 6px ${color}`,
            animation: kind === 'green' ? 'pulse 2s ease-in-out infinite' : 'none',
          }}
        />
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">SSP</span>
      </button>

      {open && (
        <>
          {/* Click-outside catcher */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />

          {/* Detail card */}
          <div
            data-testid="overview-ssp-status-detail"
            className="absolute right-0 top-9 z-40 w-[300px] rounded-lg p-3 normal-case tracking-normal text-[11px]"
            style={{
              background: '#0F0F0F',
              border: `1px solid ${color}55`,
              boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 24px ${color}22`,
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-2 pb-2 border-b border-[#1F1F1F]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-black text-gray-500">Closed Loop</p>
                <p className="text-sm font-black" style={{ color }}>{label}</p>
              </div>
              {kind === 'green' && <Check className="h-4 w-4 flex-shrink-0" style={{ color }} />}
              {kind === 'amber' && <Activity className="h-4 w-4 flex-shrink-0" style={{ color }} />}
              {kind === 'red' && <AlertCircle className="h-4 w-4 flex-shrink-0" style={{ color }} />}
            </div>

            {/* Endpoints */}
            <p className="text-[9px] uppercase tracking-[0.18em] font-black text-gray-500 mb-1.5">SSP Endpoints</p>
            <ul className="space-y-1 mb-3">
              {Object.entries(ep).map(([name, s]) => {
                const okN = s?.ok_count || 0;
                const err = !!s?.last_error;
                const dotColor = err ? RED : (okN > 0 ? GREEN : '#52525b');
                return (
                  <li key={name} className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1.5 text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
                      {name.replace(/_/g, ' ')}
                    </span>
                    <span className="font-bold tabular-nums text-gray-400">
                      {okN} / {(s?.err_count || 0)}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Capabilities */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
                style={{
                  background: health?.dna?.semantic?.configured ? `${TEAL}22` : '#1A1A1A',
                  border: `1px solid ${health?.dna?.semantic?.configured ? TEAL : BORDER_DIM}55`,
                  color: health?.dna?.semantic?.configured ? TEAL : '#71717a',
                }}
              >
                <Sparkles className="h-2.5 w-2.5" /> DNA {health?.dna?.version || 'v?'}
              </span>
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
                style={{
                  background: health?.hmac_signing ? `${PINK}22` : '#1A1A1A',
                  border: `1px solid ${health?.hmac_signing ? PINK : BORDER_DIM}55`,
                  color: health?.hmac_signing ? PINK : '#71717a',
                }}
              >
                <Lock className="h-2.5 w-2.5" /> HMAC {health?.hmac_signing ? 'signed' : 'off'}
              </span>
              {/* iter-92.9.4 — brand_embedding rescue rate.
                  level: idle | good | warning | incident.
                  A rising rescue rate is an EARLY-WARNING signal that the
                  SSP's OpenAI dependency is degraded. NOT a success story —
                  treat sustained warning/incident as an SSP-side outage. */}
              {(() => {
                const r = health?.brand_embedding_rescue;
                if (!r) return null;
                const level = r.level;
                const palette = {
                  idle:     { color: '#71717a', label: 'RESCUE idle' },
                  good:     { color: GREEN,     label: 'RESCUE 0%' },
                  warning:  { color: AMBER,     label: `RESCUE ${(r.rate * 100).toFixed(0)}%` },
                  incident: { color: RED,       label: `RESCUE ${(r.rate * 100).toFixed(0)}%` },
                }[level] || { color: '#71717a', label: 'RESCUE —' };
                const isActive = level === 'good' || level === 'warning' || level === 'incident';
                const titleText = level === 'idle'
                  ? 'No brand-embedding pushes yet this process'
                  : `${r.rescued} of ${r.ok_count} successful brand-embedding pushes needed a retry (${(r.rate * 100).toFixed(1)}%). Threshold: <8% good · 8–24% warning · ≥25% incident.`;
                return (
                  <span
                    data-testid="overview-ssp-rescue-chip"
                    data-rescue-level={level}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
                    style={{
                      background: isActive ? `${palette.color}22` : '#1A1A1A',
                      border: `1px solid ${palette.color}55`,
                      color: palette.color,
                    }}
                    title={titleText}
                  >
                    <RefreshCw className="h-2.5 w-2.5" /> {palette.label}
                  </span>
                );
              })()}
            </div>

            {detail && (
              <p className="text-[10px] text-gray-500 leading-snug">{detail}</p>
            )}
            <p className="text-[9px] text-gray-600 mt-1.5">Polls every 30s · live preview</p>
          </div>
        </>
      )}
    </div>
  );
}
